// ── watchlistOutbox ───────────────────────────────────────────────────────────
// A mark made with no signal used to be optimistic on screen, fail quietly on the
// wire, and vanish on the next reload. The outbox keeps every watchlist row patch
// that failed for a NETWORK reason (not an RLS/4xx refusal), coalesced per show,
// and replays it when the connection is back. Pure functions; the hook owns I/O.

const NETWORK_RE = /failed to fetch|networkerror|network request failed|load failed|fetch failed|network|ECONN|ETIMEDOUT|timeout|offline/i

export function isNetworkError(error, onLine = (typeof navigator !== 'undefined' ? navigator.onLine : true)) {
  if (!error) return onLine === false
  // Shape first: a coded error is a real answer from the server (RLS, constraint,
  // PostgREST, expired JWT) even if the radio happens to be off right now.
  const code = String(error.code || '')
  if (code && !/^(ECONN|ETIMEDOUT|ENOTFOUND|EAI_AGAIN)/.test(code)) return false
  if (onLine === false || error.status === 0) return true
  return NETWORK_RE.test(String(error.message || error.details || error))
}

// Later patches win key-by-key; one entry per show so a 5-episode run offline is a
// single row update, not five. `ts` is the LAST local change, used for ordering only.
export function enqueue(outbox, { showId, patch, ts = Date.now() }) {
  const list = Array.isArray(outbox) ? outbox : []
  const id = Number(showId)
  const existing = list.find(e => Number(e.showId) === id)
  if (existing) {
    return list.map(e => Number(e.showId) === id ? { showId: id, patch: { ...e.patch, ...patch }, ts } : e)
  }
  return [...list, { showId: id, patch: { ...patch }, ts }]
}

export function dequeue(outbox, showId) {
  return (outbox || []).filter(e => Number(e.showId) !== Number(showId))
}

// Overlay pending patches onto rows just fetched from the server, so a stale
// server truth never erases a change that is still waiting to sync.
export function applyOutbox(rows, outbox) {
  if (!outbox?.length || !Array.isArray(rows)) return rows
  const byId = new Map(outbox.map(e => [Number(e.showId), e.patch]))
  return rows.map(r => {
    const patch = byId.get(Number(r.show_id))
    return patch ? { ...r, ...patch } : r
  })
}

// Replays every entry through `write(entry) → Promise<{ error }>`. Returns the
// entries still owed: a network failure stays queued, a success leaves, and a
// non-network refusal is dropped (and handed to `onRefused`) because retrying a
// 4xx forever is not persistence, it is a loop.
export async function replay(outbox, write, { onRefused, onLine } = {}) {
  const remaining = []
  for (const entry of outbox || []) {
    let error
    try { error = (await write(entry))?.error } catch (e) { error = e }
    if (!error) continue
    if (isNetworkError(error, onLine)) { remaining.push(entry); continue }
    onRefused?.(entry, error)
  }
  return remaining
}

export const outboxKey = (uid) => `nextep_outbox_${uid}`

export function readOutbox(storage, uid) {
  try { const raw = storage.get(outboxKey(uid)); const v = raw ? JSON.parse(raw) : []; return Array.isArray(v) ? v : [] } catch { return [] }
}

export function writeOutbox(storage, uid, outbox) {
  try {
    if (!outbox?.length) storage.remove(outboxKey(uid))
    else storage.set(outboxKey(uid), JSON.stringify(outbox))
  } catch {}
}
